import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { LocalFile, RemoteFile } from '@types'

import { Modal } from '@components/Modal'

import { useModal } from '@hooks/app/modal.hook'
import { useFilePreviews } from '@hooks/file/file-preview.hook'
import { useObservation } from '@hooks/observation/observation.hook'
import { useUpdateObservation } from '@hooks/observation/update-observation.hook'

import { FormData, ObservationEdit } from '../../components/ObservationEdit'

type Mode = 'form' | 'localization'

export const EditObservationPage = (): JSX.Element => {
  const { hideModal } = useModal()
  const { t } = useTranslation()
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()

  const observationId = searchParams.get('observationId')

  const [mode, setMode] = useState<Mode>('form')
  const [plan, setPlan] = useState<RemoteFile>()
  const [files, setFiles] = useState<(LocalFile | RemoteFile)[]>([])

  const [formData, setFormData] = useState<FormData>({
    name: '',
    trade: undefined,
    company: undefined,
    phase: undefined,
    limitDate: undefined,
    projectFile: undefined,
    projectFilePosition: undefined,
  })

  const { data } = useObservation(observationId || '')
  const { updateObservation } = useUpdateObservation()

  const observation = data?.observation

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: formData?.projectFile?.file ? ([formData?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlan(file as RemoteFile),
  })

  const { loadFilePreviews: loadPicturesPreviews } = useFilePreviews({
    files: observation?.files as RemoteFile[],
    onFilesLoaded: setFiles,
  })

  useEffect(() => {
    if (observation) {
      setFormData({
        name: observation.name,
        trade: observation.trade,
        company: observation.company,
        phase: observation.phase,
        limitDate: observation.limitDate,
        projectFile: observation.projectFile,
        projectFilePosition: observation.projectFilePositions ? observation.projectFilePositions[0] : undefined,
      })
    }
  }, [observation])

  useEffect(() => {
    if (formData.projectFile) {
      loadPlanPreview()
    }
    if (observation?.files) {
      loadPicturesPreviews()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectFile, observation?.files, observation?.projectFile?.file])

  const handleSubmit = () => {
    if (!projectId || !observationId) return

    if (mode === 'localization') {
      setMode('form')
      return
    }

    updateObservation(
      {
        observationId,
        observation: {
          name: formData.name,
          tradeKey: formData.trade!.key,
          companyId: formData.company?.id,
          phaseKey: formData.phase?.key,
          limitDate: formData.limitDate,
          projectFileId: formData.projectFilePosition ? formData.projectFile?.id : undefined,
          projectFilePositions: formData.projectFilePosition
            ? [
                {
                  x: formData.projectFilePosition.x,
                  y: formData.projectFilePosition.y,
                },
              ]
            : undefined,
        },
      },
      files,
    )

    hideModal('update_observation')
  }

  const title =
    mode === 'form'
      ? t('modules_project-observations_pages_edit-observation_title')
      : t('modules_project-observations_pages_add-observation_localization_title')

  const submitLabel = mode === 'form' ? t('modules_project-observations_pages_add-observation_edit') : undefined

  return (
    <Modal
      title={title}
      onSubmitLabel={submitLabel}
      onSubmit={handleSubmit}
      onClose={() => hideModal('update_observation')}
      onPrevious={() => setMode('form')}
      showPrevious={mode === 'localization'}
      showClose={mode === 'form'}
      autoHideOnSubmit={false}
    >
      <ObservationEdit
        formData={formData}
        observation={observation}
        files={files}
        planFile={plan}
        projectId={projectId || ''}
        mode={mode}
        onFilesChange={setFiles}
        onFormChange={setFormData}
        onAddLocalization={() => setMode('localization')}
      />
    </Modal>
  )
}
